import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as R from 'ramda';
import axios from 'axios';
import IconButton from '@material-ui/core/IconButton';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { Modal, OverlayTrigger, Spinner, Tab, Tabs, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AdvancedSearchFilter from './AdvancedSearchFilter';
import AiEarthSearchForm from './SearchForm';
import SearchPagination from './SearchPagination';
import { applyFilterToQuery } from '../../pages/ai-earth/index';
import { search } from '../../services/firebase';
import { IElasticResult, ISearchRecord, dummyRecord } from '../../types/search';
import { CeresLogger } from '../../logger';
import {
  applyPlotLatLong,
  getFacilityDisplayLabel,
  HOMEPAGE_FACILITY_SLOT_COUNT,
  HOMEPAGE_TOAST_ADDED,
  HOMEPAGE_TOAST_ADDING,
  HOMEPAGE_TOAST_ORDER_UPDATED,
  HOMEPAGE_TOAST_REMOVED,
  HOMEPAGE_TOAST_REMOVING,
  HOMEPAGE_TOAST_SAVING_ORDER,
  isHomepageFavorite,
} from '../../util/favoriteApi.util';
import { resolveRecordLocation } from '../../util/entityLocation.util';
import {
  ADD_TO_HOMEPAGE_TOOLTIP,
  ALL_FACILITIES_LIST_HINT,
  ALL_FACILITIES_SECTION,
  formatHomepageSelectedCount,
  HOMEPAGE_DRAG_REORDER_HINT,
  HOMEPAGE_INSTRUCTIONS,
  HOMEPAGE_PANEL_HINT,
  HOMEPAGE_SLOT_EMPTY,
  HOMEPAGE_SLOT_LABELS,
  homepageSectionTitle,
  MANAGE_FACILITIES_HELPER,
  MANAGE_FACILITIES_TITLE,
  REMOVE_FROM_HOMEPAGE_TOOLTIP,
  REMOVE_FACILITIES_HELPER,
  REMOVE_FACILITIES_LIST_HINT,
  REMOVE_FACILITIES_TITLE,
  SHOWN_ON_HOMEPAGE_BADGE,
} from '../../util/facilityHomepageCopy';
import {
  completeHomepagePendingToast,
  dismissHomepagePendingToast,
  showHomepagePendingToast,
} from '../../util/homepageFacilityToast.util';

type AddFacilityTab = 'search-facilities' | 'saved-facilities';
type AddFacilityModalMode = 'manage' | 'remove';

const SEARCH_FACILITIES_TAB = 'Search facilities';
const SAVED_FACILITIES_TAB = 'Saved facilities';
const SEARCH_IDLE_HINT = 'Search by facility name, RN number, or location to add a site.';
const EMPTY_SAVED_FACILITIES =
  'No facilities saved yet. Add a facility to get started.';
const EMPTY_SEARCH_RESULTS = 'No facilities found. Try a different search term.';
const ADD_SAVED_FACILITY_TOOLTIP = 'Add to saved facilities';
const REMOVE_SAVED_FACILITY_TOOLTIP = 'Remove from saved facilities';

/** Keep removed facilities visible in My Facilities with an unfilled star. */
const RECENTLY_REMOVED_GRACE_MS = 3 * 60 * 1000;

type RecentlyRemovedFavorite = {
  record: ISearchRecord;
  removedAt: number;
  sortIndex: number;
};

type FacilityListRow = {
  key: string;
  entityName: string;
  entityNumber: string;
  isFav: boolean;
  busy: boolean;
  onToggle: () => void;
  isOnHomepage?: boolean;
  homepageBusy?: boolean;
  onPromote?: () => void;
  onDemote?: () => void;
};

interface AddFacilityModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: AddFacilityTab;
  mode?: AddFacilityModalMode;
  onRequestAllFavorites?: () => void | Promise<void>;
  totalFavoriteCount?: number;
  favoritesLoading?: boolean;
  onAdd: (record: ISearchRecord) => Promise<void>;
  onRemove: (contentId: string) => Promise<void>;
  onPromoteToHomepage: (contentId: string) => Promise<boolean>;
  onDemoteFromHomepage: (contentId: string) => Promise<boolean>;
  onReorderHomepageSlot: (
    draggedContentId: string,
    targetSlotIndex: number
  ) => Promise<boolean>;
  isHomepageOrderSaving?: boolean;
  favoriteContentIds: string[];
  favorites: ISearchRecord[];
}

const getUniqueSearchEntityRecords = (records: ISearchRecord[]) =>
  R.compose<ISearchRecord[], ISearchRecord[], ISearchRecord[]>(
    R.uniqBy(R.prop('entityNumber')),
    R.filter((record: ISearchRecord) => !!record && !!record.entityNumber)
  )(records);

const plotRecordLocations = (records: ISearchRecord[]) => {
  records.forEach((ele, index) => {
    records[index] = applyPlotLatLong(ele);
  });
};

const constructChemPlantQuery = (queryText: string, filter: any = {}) => {
  let query = `v_keyword=${encodeURIComponent(queryText)}&`;
  if (Object.keys(filter || {}).length === 0) {
    return query;
  }

  if (filter.rnNumber) {
    query += `v_entity_number=${filter.rnNumber}&`;
  }
  if (filter.cityName) {
    query += `v_city=${filter.cityName}&`;
  }
  if (filter.state?.label) {
    const stateSymbol = filter.state.label.substring(
      filter.state.label.indexOf('(') + 1,
      filter.state.label.indexOf(')')
    );
    query += `v_state=${stateSymbol}&`;
  }
  if (filter.zipCode) {
    query += `v_zip_code=${filter.zipCode}`;
  }

  return query;
};

const buildRecordFromEntity = async (entity: any): Promise<ISearchRecord> => {
  const record: ISearchRecord = {
    ...dummyRecord,
    entityName: entity.entity_name,
    entityNumber: entity.entity_number,
    contentId: entity.content_id,
    location: { lat: 0, lon: 0, geomz: '', geom_json: {} },
  };

  return resolveRecordLocation(record, { includeGeomJson: true });
};

const AddFacilityModal: React.FC<AddFacilityModalProps> = ({
  open,
  onClose,
  onAdd,
  onRemove,
  onPromoteToHomepage,
  onDemoteFromHomepage,
  onReorderHomepageSlot,
  isHomepageOrderSaving = false,
  initialTab = 'search-facilities',
  mode = 'manage',
  onRequestAllFavorites,
  totalFavoriteCount = 0,
  favoritesLoading = false,
  favoriteContentIds,
  favorites,
}) => {
  const isRemoveMode = mode === 'remove';
  const removeLoadAttemptsRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pageStartsRef = useRef<number[]>([0]);
  const [activeTab, setActiveTab] = useState<AddFacilityTab>('search-facilities');
  const [filter, setFilter] = useState<any>({});
  const [showFilter, setShowFilter] = useState(false);
  const [currentQueryText, setCurrentQueryText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<IElasticResult<ISearchRecord> | null>(null);
  const [chemPlantResults, setChemPlantResults] = useState<any[]>([]);
  const [toggleBusyId, setToggleBusyId] = useState<string | null>(null);
  const [homepageActionBusyId, setHomepageActionBusyId] = useState<string | null>(null);
  const [draggedSlotContentId, setDraggedSlotContentId] = useState<string | null>(null);
  const draggedSlotContentIdRef = useRef<string | null>(null);
  const homepageDropSlotRef = useRef<number | null>(null);
  const slotDragFinalizeRef = useRef(false);
  const [recentlyRemoved, setRecentlyRemoved] = useState<RecentlyRemovedFavorite[]>([]);
  const [optimisticAddedIds, setOptimisticAddedIds] = useState<Set<string>>(() => new Set());

  const recentlyRemovedIdSet = useMemo(
    () => new Set(recentlyRemoved.map((item) => item.record.contentId)),
    [recentlyRemoved]
  );

  const checkIsFav = (contentId: string) => {
    if (recentlyRemovedIdSet.has(contentId)) {
      return false;
    }
    if (optimisticAddedIds.has(contentId)) {
      return true;
    }
    return favoriteContentIds.includes(contentId);
  };

  const rememberRemovedFavorite = (record: ISearchRecord) => {
    const sortIndex = favorites.findIndex((favorite) => favorite.contentId === record.contentId);
    setRecentlyRemoved((prev) => {
      const filtered = prev.filter((item) => item.record.contentId !== record.contentId);
      return [
        ...filtered,
        {
          record,
          removedAt: Date.now(),
          sortIndex: sortIndex >= 0 ? sortIndex : favorites.length + filtered.length,
        },
      ];
    });
  };

  const forgetRemovedFavorite = (contentId: string) => {
    setRecentlyRemoved((prev) => prev.filter((item) => item.record.contentId !== contentId));
  };

  const pruneExpiredRemovedFavorites = () => {
    const now = Date.now();
    setRecentlyRemoved((prev) =>
      prev.filter((item) => now - item.removedAt < RECENTLY_REMOVED_GRACE_MS)
    );
  };

  const uniqueEntityRecords = useMemo(
    () => getUniqueSearchEntityRecords(searchResult?.records || []),
    [searchResult]
  );

  const resetState = () => {
    pageStartsRef.current = [0];
    setActiveTab('search-facilities');
    setFilter({});
    setShowFilter(false);
    setCurrentQueryText('');
    setSearchResult(null);
    setChemPlantResults([]);
    setToggleBusyId(null);
    setHomepageActionBusyId(null);
    draggedSlotContentIdRef.current = null;
    homepageDropSlotRef.current = null;
    setDraggedSlotContentId(null);
    setRecentlyRemoved([]);
    setOptimisticAddedIds(new Set());
  };

  useEffect(() => {
    setOptimisticAddedIds((prev) => {
      if (!prev.size) {
        return prev;
      }
      const next = new Set(prev);
      prev.forEach((contentId) => {
        if (favoriteContentIds.includes(contentId)) {
          next.delete(contentId);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [favoriteContentIds]);

  useEffect(() => {
    if (!open) {
      removeLoadAttemptsRef.current = 0;
      return undefined;
    }
    setActiveTab(initialTab);
    pruneExpiredRemovedFavorites();
    const intervalId = window.setInterval(pruneExpiredRemovedFavorites, 30_000);
    return () => window.clearInterval(intervalId);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open || !isRemoveMode || !onRequestAllFavorites) {
      return;
    }
    if (favoritesLoading) {
      return;
    }
    if (totalFavoriteCount > 0 && favorites.length >= totalFavoriteCount) {
      return;
    }
    if (removeLoadAttemptsRef.current >= 2) {
      return;
    }
    removeLoadAttemptsRef.current += 1;
    void onRequestAllFavorites();
  }, [
    open,
    isRemoveMode,
    onRequestAllFavorites,
    favorites.length,
    totalFavoriteCount,
    favoritesLoading,
  ]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const SEARCH_PAGE_SIZE = 5;
  const ES_BATCH_SIZE = 20;
  const MAX_CHEM_ON_FIRST_PAGE = 2;

  const fetchChemPlantResults = async (queryText: string) => {
    const chemPlantQuery = constructChemPlantQuery(queryText, filter);
    const chemPlantsResponse: any = await axios.get(
      `${process.env.REACT_APP_CERES_URL}/EntitySearch?${chemPlantQuery}&v_epn_only=1`
    );
    if (chemPlantsResponse.data?.error !== 'No Entity Found!' && Array.isArray(chemPlantsResponse.data?.entities)) {
      return chemPlantsResponse.data.entities.slice(0, MAX_CHEM_ON_FIRST_PAGE);
    }
    return [];
  };

  const fetchDocumentSearch = async (queryText: string, pageStartFrom = 0) => {
    const uniqueRecords: ISearchRecord[] = [];
    const seenEntityNumbers = new Set<string>();
    let esFrom = pageStartFrom;
    let totalHits = 0;

    while (uniqueRecords.length < SEARCH_PAGE_SIZE) {
      const result = await search(
        applyFilterToQuery(
          { query: { from: esFrom, text: queryText, size: ES_BATCH_SIZE } },
          filter || {}
        )
      );
      const data = result.data as IElasticResult<ISearchRecord>;
      const batch = data.records || [];
      totalHits =
        typeof data.total === 'object'
          ? (data.total as { value?: number }).value ?? 0
          : Number(data.total) || 0;

      if (!batch.length) {
        break;
      }

      batch.forEach((record) => {
        if (!record?.entityNumber || seenEntityNumbers.has(record.entityNumber)) {
          return;
        }
        seenEntityNumbers.add(record.entityNumber);
        uniqueRecords.push(record);
      });

      esFrom += batch.length;
      if (esFrom >= totalHits || batch.length < ES_BATCH_SIZE) {
        break;
      }
    }

    plotRecordLocations(uniqueRecords);

    return {
      records: uniqueRecords.slice(0, SEARCH_PAGE_SIZE),
      pageStartFrom,
      nextFrom: esFrom,
      isLastPage: esFrom >= totalHits || uniqueRecords.length < SEARCH_PAGE_SIZE,
      total: totalHits,
      from: pageStartFrom,
      size: SEARCH_PAGE_SIZE,
    };
  };

  const doTextSearch = async (queryText: string, pageStartFrom = 0) => {
    if (!queryText && !Object.keys(filter || {}).length) {
      return;
    }
    setCurrentQueryText(queryText);
    setIsSearching(true);
    try {
      if (pageStartFrom === 0) {
        pageStartsRef.current = [0];
      } else {
        const idx = pageStartsRef.current.indexOf(pageStartFrom);
        if (idx === -1) {
          pageStartsRef.current.push(pageStartFrom);
        } else {
          pageStartsRef.current = pageStartsRef.current.slice(0, idx + 1);
        }
      }

      const [documentResult, chemPlants] = await Promise.all([
        fetchDocumentSearch(queryText, pageStartFrom),
        pageStartFrom === 0 ? fetchChemPlantResults(queryText) : Promise.resolve([]),
      ]);
      setSearchResult(documentResult as IElasticResult<ISearchRecord>);
      setChemPlantResults(chemPlants);
    } catch (error) {
      CeresLogger.error(error as any);
      toast.error('Error while searching facilities.');
      setSearchResult(null);
      setChemPlantResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleRecordFavoriteToggle = async (record: ISearchRecord, isFav: boolean) => {
    if (isFav) {
      rememberRemovedFavorite(record);
    } else {
      forgetRemovedFavorite(record.contentId);
      setOptimisticAddedIds((prev) => new Set(prev).add(record.contentId));
    }
    setToggleBusyId(record.contentId);
    try {
      if (isFav) {
        await onRemove(record.contentId);
      } else {
        await onAdd(record);
      }
    } catch {
      if (isFav) {
        forgetRemovedFavorite(record.contentId);
      } else {
        setOptimisticAddedIds((prev) => {
          const next = new Set(prev);
          next.delete(record.contentId);
          return next;
        });
      }
      // Parent shows toast.
    } finally {
      setToggleBusyId(null);
    }
  };

  const handleChemFavoriteToggle = async (chemPlantItem: any, isFav: boolean) => {
    const contentId = chemPlantItem.content_id;
    if (isFav) {
      const existingFavorite = favorites.find((favorite) => favorite.contentId === contentId);
      const recordForGrace =
        existingFavorite
        ?? ({
          ...dummyRecord,
          contentId,
          entityName: chemPlantItem.entity_name,
          entityNumber: chemPlantItem.entity_number,
        } as ISearchRecord);
      rememberRemovedFavorite(recordForGrace);
    } else {
      forgetRemovedFavorite(contentId);
      setOptimisticAddedIds((prev) => new Set(prev).add(contentId));
    }
    setToggleBusyId(contentId);
    try {
      if (isFav) {
        await onRemove(contentId);
      } else {
        const record = await buildRecordFromEntity(chemPlantItem);
        await onAdd(record);
      }
    } catch {
      if (isFav) {
        forgetRemovedFavorite(contentId);
      } else {
        setOptimisticAddedIds((prev) => {
          const next = new Set(prev);
          next.delete(contentId);
          return next;
        });
      }
      // Parent shows toast.
    } finally {
      setToggleBusyId(null);
    }
  };

  const chemPlantEntityNumbers = new Set(
    uniqueEntityRecords.map((record) => record.entityNumber).filter(Boolean)
  );
  const filteredChemPlantResults = chemPlantResults.filter(
    (item) => !chemPlantEntityNumbers.has(item.entity_number)
  );

  const hasResults = uniqueEntityRecords.length > 0 || filteredChemPlantResults.length > 0;
  const showEmptyState = !isSearching && currentQueryText && !hasResults;
  const showResultsList = !isSearching && (hasResults || showEmptyState);

  const handleHomepagePromote = async (contentId: string) => {
    if (isHomepageOrderSaving || homepageActionBusyId) {
      return;
    }
    setHomepageActionBusyId(contentId);
    showHomepagePendingToast(HOMEPAGE_TOAST_ADDING);
    try {
      const saved = await onPromoteToHomepage(contentId);
      if (saved) {
        completeHomepagePendingToast(HOMEPAGE_TOAST_ADDED);
      } else {
        dismissHomepagePendingToast();
      }
    } catch {
      dismissHomepagePendingToast();
    } finally {
      setHomepageActionBusyId(null);
    }
  };

  const handleHomepageDemote = async (contentId: string) => {
    if (isHomepageOrderSaving || homepageActionBusyId) {
      return;
    }
    setHomepageActionBusyId(contentId);
    showHomepagePendingToast(HOMEPAGE_TOAST_REMOVING);
    try {
      const saved = await onDemoteFromHomepage(contentId);
      if (saved) {
        completeHomepagePendingToast(HOMEPAGE_TOAST_REMOVED);
      } else {
        dismissHomepagePendingToast();
      }
    } catch {
      dismissHomepagePendingToast();
    } finally {
      setHomepageActionBusyId(null);
    }
  };

  const homepagePreviewSlots = favorites.slice(0, HOMEPAGE_FACILITY_SLOT_COUNT);
  const homepageFilledCount = homepagePreviewSlots.filter(Boolean).length;
  const canReorderHomepageSlots =
    homepageFilledCount > 1 && !isHomepageOrderSaving && !homepageActionBusyId;

  const resetHomepageSlotDragState = () => {
    draggedSlotContentIdRef.current = null;
    homepageDropSlotRef.current = null;
    setDraggedSlotContentId(null);
  };

  const onHomepageSlotDragStart = (
    event: React.DragEvent,
    contentId: string,
    slotIndex: number
  ) => {
    if (!canReorderHomepageSlots || slotIndex >= HOMEPAGE_FACILITY_SLOT_COUNT) {
      event.preventDefault();
      return;
    }
    draggedSlotContentIdRef.current = contentId;
    homepageDropSlotRef.current = null;
    setDraggedSlotContentId(contentId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', contentId);
  };

  const onHomepageSlotDragOver = (event: React.DragEvent, slotIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    if (!draggedSlotContentIdRef.current) {
      return;
    }
    homepageDropSlotRef.current = slotIndex;
  };

  const onHomepageSlotDragLeave = (event: React.DragEvent, slotIndex: number) => {
    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
      return;
    }
    if (homepageDropSlotRef.current === slotIndex) {
      homepageDropSlotRef.current = null;
    }
  };

  const finalizeHomepageSlotDrag = async () => {
    if (slotDragFinalizeRef.current) {
      return;
    }
    const draggedId = draggedSlotContentIdRef.current;
    const targetSlot = homepageDropSlotRef.current;
    if (draggedId == null || targetSlot == null) {
      resetHomepageSlotDragState();
      return;
    }
    slotDragFinalizeRef.current = true;
    resetHomepageSlotDragState();
    showHomepagePendingToast(HOMEPAGE_TOAST_SAVING_ORDER);
    try {
      const saved = await onReorderHomepageSlot(draggedId, targetSlot);
      if (saved) {
        completeHomepagePendingToast(HOMEPAGE_TOAST_ORDER_UPDATED);
      } else {
        dismissHomepagePendingToast();
      }
    } catch {
      dismissHomepagePendingToast();
    } finally {
      slotDragFinalizeRef.current = false;
    }
  };

  const onHomepageSlotDragEnd = () => {
    void finalizeHomepageSlotDrag();
  };

  const onHomepageSlotDrop = (event: React.DragEvent, slotIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    homepageDropSlotRef.current = slotIndex;
    void finalizeHomepageSlotDrag();
  };

  const renderFacilityList = (rows: FacilityListRow[], showHomepageActions = false) => {
    if (!rows.length) {
      return null;
    }

    return (
      <div className="cmn-tracker-card-list" role="list">
        {rows.map((row) => (
          <div
            key={row.key}
            className={`cmn-tracker-card${row.isOnHomepage ? ' cmn-tracker-card--homepage' : ''}`}
            role="listitem"
          >
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 100 }}
              overlay={
                <Tooltip id={`facility-star-${row.key}`} className="cmn-ai-earth-tooltip">
                  {row.isFav ? REMOVE_SAVED_FACILITY_TOOLTIP : ADD_SAVED_FACILITY_TOOLTIP}
                </Tooltip>
              }
            >
              <span className="cmn-tracker-card__fav-wrap">
                <IconButton
                  onClick={row.onToggle}
                  aria-label={row.isFav ? REMOVE_SAVED_FACILITY_TOOLTIP : ADD_SAVED_FACILITY_TOOLTIP}
                  component="span"
                  disabled={row.busy || !!homepageActionBusyId || isHomepageOrderSaving}
                  size="small"
                  className="cmn-tracker-card__fav"
                >
                  {row.isFav ? (
                    <StarIcon className="cmn-star-gold" />
                  ) : (
                    <StarBorderIcon className="cmn-star-gold" />
                  )}
                </IconButton>
              </span>
            </OverlayTrigger>
            <OverlayTrigger
              placement="bottom"
              delay={{ show: 250, hide: 100 }}
              overlay={
                <Tooltip id={`facility-name-${row.key}`} className="cmn-ai-earth-tooltip">
                  {row.entityNumber}
                </Tooltip>
              }
            >
              <span className="cmn-tracker-card__name">{row.entityName}</span>
            </OverlayTrigger>
            {showHomepageActions && row.isFav && (
              <span className="cmn-tracker-card__actions">
                {row.isOnHomepage ? (
                  <span className="cmn-tracker-card__homepage-badge">{SHOWN_ON_HOMEPAGE_BADGE}</span>
                ) : (
                  <OverlayTrigger
                    placement="left"
                    delay={{ show: 250, hide: 100 }}
                    overlay={
                      <Tooltip id={`promote-${row.key}`} className="cmn-ai-earth-tooltip">
                        {ADD_TO_HOMEPAGE_TOOLTIP}
                      </Tooltip>
                    }
                  >
                    <span>
                      <IconButton
                        onClick={() => void handleHomepagePromote(row.key)}
                        aria-label={ADD_TO_HOMEPAGE_TOOLTIP}
                        component="span"
                        disabled={
                          row.homepageBusy
                          || !!homepageActionBusyId
                          || isHomepageOrderSaving
                        }
                        size="small"
                        className="cmn-tracker-card__homepage-action"
                      >
                        {row.homepageBusy ? (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <ArrowUpwardIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </OverlayTrigger>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const searchPageStartFromForRows = (searchResult as { pageStartFrom?: number } | null)?.pageStartFrom ?? 0;
  const remainingEntitySlots = Math.max(0, SEARCH_PAGE_SIZE - uniqueEntityRecords.length);
  const chemRowsForPage =
    searchPageStartFromForRows === 0
      ? filteredChemPlantResults.slice(0, Math.min(MAX_CHEM_ON_FIRST_PAGE, remainingEntitySlots))
      : [];

  const searchTableRowsAll = [
    ...uniqueEntityRecords.map((record) => {
      const isFav = checkIsFav(record.contentId);
      return {
        key: record.contentId,
        entityName: record.entityName || record.entityNumber,
        entityNumber: record.entityNumber,
        isFav,
        busy: toggleBusyId === record.contentId,
        onToggle: () => handleRecordFavoriteToggle(record, isFav),
      };
    }),
    ...chemRowsForPage.map((chemPlantItem) => {
      const isFav = checkIsFav(chemPlantItem.content_id);
      return {
        key: chemPlantItem.content_id || chemPlantItem.entity_number,
        entityName: chemPlantItem.entity_name,
        entityNumber: chemPlantItem.entity_number,
        isFav,
        busy: toggleBusyId === chemPlantItem.content_id,
        onToggle: () => handleChemFavoriteToggle(chemPlantItem, isFav),
      };
    }),
  ];

  const searchTableRows = searchTableRowsAll.slice(0, SEARCH_PAGE_SIZE);

  const searchTotalHits =
    searchResult?.total != null
      ? (searchResult.total as { value?: number }).value ?? searchResult.total
      : 0;

  const searchNextFrom = (searchResult as { nextFrom?: number } | null)?.nextFrom ?? 0;
  const searchIsLastPage = (searchResult as { isLastPage?: boolean } | null)?.isLastPage ?? true;
  const canGoToPreviousPage = pageStartsRef.current.indexOf(searchPageStartFromForRows) > 0;

  const myFacilitiesDisplayItems = useMemo(() => {
    const now = Date.now();
    const items: { record: ISearchRecord; isFav: boolean; sortIndex: number }[] = favorites.map(
      (favorite, index) => ({
        record: favorite,
        isFav:
          !recentlyRemovedIdSet.has(favorite.contentId)
          && (favoriteContentIds.includes(favorite.contentId) || optimisticAddedIds.has(favorite.contentId)),
        sortIndex: index,
      })
    );
    const listedIds = new Set(items.map((item) => item.record.contentId));

    recentlyRemoved.forEach((item) => {
      if (now - item.removedAt >= RECENTLY_REMOVED_GRACE_MS) {
        return;
      }
      if (listedIds.has(item.record.contentId)) {
        return;
      }
      items.push({
        record: item.record,
        isFav: false,
        sortIndex: item.sortIndex + 0.5,
      });
    });

    return items.sort((a, b) =>
      getFacilityDisplayLabel(a.record).localeCompare(
        getFacilityDisplayLabel(b.record),
        undefined,
        { sensitivity: 'base' }
      )
    );
  }, [favorites, favoriteContentIds, recentlyRemoved, recentlyRemovedIdSet, optimisticAddedIds]);

  const favoritesTableRows = myFacilitiesDisplayItems.map((item) => ({
    key: item.record.contentId,
    entityName: item.record.displayName || item.record.entityName || item.record.entityNumber,
    entityNumber: item.record.entityNumber,
    isFav: item.isFav,
    busy: toggleBusyId === item.record.contentId,
    onToggle: () => handleRecordFavoriteToggle(item.record, item.isFav),
    isOnHomepage: isHomepageFavorite(favorites, item.record.contentId),
    homepageBusy: homepageActionBusyId === item.record.contentId,
  }));

  const hasMyFacilitiesToShow = favoritesTableRows.length > 0;
  const savedFacilitiesTabTitle =
    favorites.length > 0 ? `${SAVED_FACILITIES_TAB} (${favorites.length})` : SAVED_FACILITIES_TAB;
  const removeFacilitiesCount = totalFavoriteCount > 0 ? totalFavoriteCount : favorites.length;
  const removeFacilitiesSectionTitle =
    removeFacilitiesCount > 0
      ? `${ALL_FACILITIES_SECTION} (${removeFacilitiesCount})`
      : ALL_FACILITIES_SECTION;
  const isRemoveListLoading =
    isRemoveMode &&
    favoritesLoading &&
    (totalFavoriteCount === 0 || favorites.length < totalFavoriteCount);

  const getFacilityLabel = (record: ISearchRecord) =>
    record.displayName || record.entityName || record.entityNumber;

  const savedHomepageSectionTitle = homepageSectionTitle();

  const renderHomepageSlotsPreview = () => (
    <div className="cmn-add-facility-homepage-preview" aria-label={savedHomepageSectionTitle}>
      <div className="cmn-add-facility-homepage-preview__header">
        <h4 className="cmn-add-facility-homepage-preview__title">{savedHomepageSectionTitle}</h4>
        <span className="cmn-add-facility-homepage-preview__count">
          {formatHomepageSelectedCount(homepagePreviewSlots.length)}
        </span>
      </div>
      <p className="cmn-add-facility-homepage-preview__hint">{HOMEPAGE_PANEL_HINT}</p>
      {canReorderHomepageSlots && (
        <p className="cmn-add-facility-homepage-preview__hint cmn-add-facility-homepage-preview__hint--drag">
          {HOMEPAGE_DRAG_REORDER_HINT}
        </p>
      )}
      <ul className="cmn-add-facility-homepage-preview__slots">
        {HOMEPAGE_SLOT_LABELS.map(({ slot, role }, slotIndex) => {
          const favorite = homepagePreviewSlots[slotIndex];
          const isDragging = favorite && draggedSlotContentId === favorite.contentId;
          return (
            <li
              key={slot}
              className={`cmn-add-facility-homepage-preview__slot${
                isDragging ? ' cmn-add-facility-homepage-preview__slot--dragging' : ''
              }${favorite && canReorderHomepageSlots ? ' cmn-add-facility-homepage-preview__slot--draggable' : ''}`}
              draggable={!!favorite && canReorderHomepageSlots}
              onDragStart={(event) =>
                favorite && onHomepageSlotDragStart(event, favorite.contentId, slotIndex)
              }
              onDragEnd={onHomepageSlotDragEnd}
              onDragOver={(event) => onHomepageSlotDragOver(event, slotIndex)}
              onDragLeave={(event) => onHomepageSlotDragLeave(event, slotIndex)}
              onDrop={(event) => onHomepageSlotDrop(event, slotIndex)}
            >
              <div className="cmn-add-facility-homepage-preview__slot-main">
                <span className="cmn-add-facility-homepage-preview__slot-label">
                  {slot} · {role}
                </span>
                <span
                  className={
                    favorite
                      ? 'cmn-add-facility-homepage-preview__slot-name'
                      : 'cmn-add-facility-homepage-preview__slot-empty'
                  }
                >
                  {favorite ? getFacilityLabel(favorite) : HOMEPAGE_SLOT_EMPTY}
                </span>
              </div>
              {favorite && (
                <OverlayTrigger
                  placement="left"
                  delay={{ show: 250, hide: 100 }}
                  overlay={
                    <Tooltip id={`demote-slot-${slotIndex}`} className="cmn-ai-earth-tooltip">
                      {REMOVE_FROM_HOMEPAGE_TOOLTIP}
                    </Tooltip>
                  }
                >
                  <span>
                    <IconButton
                      onClick={() => void handleHomepageDemote(favorite.contentId)}
                      aria-label={REMOVE_FROM_HOMEPAGE_TOOLTIP}
                      component="span"
                      disabled={
                        homepageActionBusyId === favorite.contentId || isHomepageOrderSaving
                      }
                      size="small"
                      className="cmn-add-facility-homepage-preview__remove"
                    >
                      {homepageActionBusyId === favorite.contentId ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <ArrowDownwardIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </OverlayTrigger>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <>
      <Modal
        show={open}
        onHide={handleClose}
        centered
        className="cmn-add-facility-modal"
        dialogClassName="cmn-add-facility-modal__dialog"
        contentClassName="cmn-add-facility-modal__content"
      >
        <Modal.Body>
          <div className="cmn-add-facility-tabs-wrap">
            <button
              type="button"
              className="close cmn-add-facility-close"
              aria-label="Close"
              onClick={handleClose}
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <header className="cmn-add-facility-header">
              <h3 className="cmn-add-facility-header__title">
                {isRemoveMode ? REMOVE_FACILITIES_TITLE : MANAGE_FACILITIES_TITLE}
              </h3>
              <p className="cmn-add-facility-header__helper">
                {isRemoveMode ? REMOVE_FACILITIES_HELPER : MANAGE_FACILITIES_HELPER}
              </p>
              {!isRemoveMode && (
                <p className="cmn-add-facility-header__subtitle">
                  {HOMEPAGE_INSTRUCTIONS.map((line, index) => (
                    <React.Fragment key={line}>
                      {index > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
                </p>
              )}
            </header>
            {isRemoveMode ? (
              <div className="cmn-add-facility-results cmn-add-facility-results--facilities cmn-add-facility-results--remove">
                <h4 className="cmn-add-facility-saved-section-title">{removeFacilitiesSectionTitle}</h4>
                <p className="cmn-add-facility-tab-hint">{REMOVE_FACILITIES_LIST_HINT}</p>
                {isRemoveListLoading && favorites.length === 0 ? (
                  <div className="cmn-add-facility-spinner">
                    <Spinner as="span" variant="primary" animation="border" role="status" aria-hidden="true" />
                  </div>
                ) : !hasMyFacilitiesToShow ? (
                  <p className="cmn-add-facility-empty">{EMPTY_SAVED_FACILITIES}</p>
                ) : (
                  <>
                    {renderFacilityList(favoritesTableRows, false)}
                    {isRemoveListLoading && (
                      <div className="cmn-add-facility-spinner cmn-add-facility-spinner--inline">
                        <Spinner as="span" variant="primary" animation="border" size="sm" role="status" aria-hidden="true" />
                        <span>Loading saved facilities…</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
            <Tabs
              id="add-facility-tabs"
              activeKey={activeTab}
              onSelect={(key) => key && setActiveTab(key as AddFacilityTab)}
            >
              <Tab eventKey="search-facilities" title={SEARCH_FACILITIES_TAB}>
                <div className="cmn-add-facility-search-wrap">
                  <p className="cmn-add-facility-tab-hint">{SEARCH_IDLE_HINT}</p>
                  <AiEarthSearchForm
                    isFilterApplied={filter}
                    onFilterClick={() => setShowFilter(true)}
                    isLoading={isSearching}
                    inputRef={searchInputRef}
                    currentQueryText={currentQueryText}
                    onSearchSubmit={(queryText: string) => doTextSearch(queryText, 0)}
                  />
                </div>

                {(currentQueryText || isSearching) && (
                  <div className="cmn-add-facility-results">
                    {isSearching && !showResultsList && (
                      <div className="cmn-add-facility-spinner">
                        <Spinner as="span" variant="primary" animation="border" role="status" aria-hidden="true" />
                      </div>
                    )}

                    {showResultsList && (
                      <>
                        {showEmptyState && (
                          <p className="cmn-add-facility-empty">{EMPTY_SEARCH_RESULTS}</p>
                        )}

                        {renderFacilityList(searchTableRows)}

                        {searchResult &&
                        (Number(searchTotalHits) > SEARCH_PAGE_SIZE || !searchIsLastPage) ? (
                          <div className="cmn-add-facility-pagination">
                            <p className="cmn-add-facility-page-hint">
                              Showing {searchTableRows.length} of {Number(searchTotalHits).toLocaleString()} matches
                            </p>
                            <SearchPagination
                              isDisabled={isSearching}
                              isFirstPage={!canGoToPreviousPage}
                              isLastPage={searchIsLastPage}
                              totalRecords={Number(searchTotalHits)}
                              pageSize={SEARCH_PAGE_SIZE}
                              compactLabel
                              onPreviousClick={() => {
                                const idx = pageStartsRef.current.indexOf(searchPageStartFromForRows);
                                if (idx > 0) {
                                  doTextSearch(currentQueryText, pageStartsRef.current[idx - 1]);
                                }
                              }}
                              onNextClick={() => doTextSearch(currentQueryText, searchNextFrom)}
                            />
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
              </Tab>
              <Tab eventKey="saved-facilities" title={savedFacilitiesTabTitle}>
                <div className="cmn-add-facility-results cmn-add-facility-results--facilities">
                  {renderHomepageSlotsPreview()}
                  <h4 className="cmn-add-facility-saved-section-title">{ALL_FACILITIES_SECTION}</h4>
                  <p className="cmn-add-facility-tab-hint">{ALL_FACILITIES_LIST_HINT}</p>
                  {!hasMyFacilitiesToShow ? (
                    <p className="cmn-add-facility-empty">{EMPTY_SAVED_FACILITIES}</p>
                  ) : (
                    renderFacilityList(favoritesTableRows, true)
                  )}
                </div>
              </Tab>
            </Tabs>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <AdvancedSearchFilter
        onFilterClear={() => setFilter({})}
        onFilterApply={(newFilter) => {
          setFilter(newFilter);
          setShowFilter(false);
          if (currentQueryText) {
            doTextSearch(currentQueryText, 0);
          }
        }}
        onClose={() => setShowFilter(false)}
        show={open && showFilter}
      />
    </>
  );
};

export default AddFacilityModal;
